import { ref, computed } from 'vue';
import { taskMeta } from './taskMeta';

/**
 * PriorityStack (v3 homepage) — the manager's OWN "what needs me now" stack: the
 * 1–3 most urgent of the user's open tasks, ranked sent-back → in-progress →
 * ready (due-soonest first), as full-width horizontal cards. Distinct from
 * AdminPriority (which is the cross-user admin decision queue): this is the
 * person's own work. Mirrors the prototype V3PriorityStack exactly — same
 * `.v3-pcard` layout, lifecycle stepper, solid status pill, inline sent-back
 * feedback, and a context-aware CTA driven by the task TYPE (never assumes
 * "evaluation").
 *
 * Framework-agnostic, `self` pattern. Tasks are injected as a GETTER (same
 * source as taskList) so the composable never imports a store. Navigation stays
 * in the presentation layer.
 *
 * @param {Object} options
 * @param {() => any[]}   options.getTasks   the user's open tasks
 * @param {() => boolean} [options.getLoading]
 * @param {() => number}  [options.getLimit] cards before "View all" (default 3)
 */

// Task type → icon/verb/ask/mins comes from the shared taskMeta vocabulary.

// Lifecycle stepper (prototype V3StageStepper): 4 coarse stages + the index each
// status sits at. Sent-back bounces back to "Submitted" with a warning tone.
const LIFECYCLE_IDX = { ready: 0, 'not-started': 0, 'in-progress': 1, returned: 2, submitted: 2, approved: 3 };
const STEP_COUNT = 4;

// status → solid status pill (prototype V3_SPILL subset for the three states a
// user's own task can be in here).
const SPILL = {
  ready: { label: 'Open', tone: 'todo', icon: 'radio_button_unchecked' },
  'in-progress': { label: 'In progress', tone: 'accent', icon: 'schedule' },
  returned: { label: 'Sent back', tone: 'violet', icon: 'undo' },
};

export function priorityStack(options) {
  const { getTasks = () => [], getLoading = () => false, getLimit = () => 3 } = options || {};
  // Cards shown (prototype `max`, default 3) — configurable via widget settings.
  const limit = () => getLimit() || 3;

  const self = { showAll: ref(false) };

  self.isLoading = computed(() => !!getLoading());

  // The actionable state comes from the doc's LIFECYCLE status name (client-
  // configured) — `task_status` is only Open/Completed/Cancelled/Unassigned and
  // carries no in-progress/returned semantics. Heuristic name match, same as the
  // review-queue mapping (a per-tenant status→key map would make it exact).
  const statusOf = (t) => {
    const s = String((t && (t.lifecycle_status_name || t.doc_status_text)) || '').toLowerCase();
    if (/return|sent back|revis|reject|bounce/.test(s)) return 'returned';
    if (/progress|draft|started|working/.test(s)) return 'in-progress';
    if (/wait|await|pending|review|approv|with /.test(s)) return 'waiting';
    return 'ready';
  };
  const dueDays = (t) => {
    if (!t || !t.due_date) return null;
    const d = new Date(t.due_date);
    return isNaN(d.getTime()) ? null : Math.floor((d.getTime() - Date.now()) / 86400000);
  };
  const dueSoon = (t) => {
    const d = dueDays(t);
    return d !== null && d <= 7;
  };
  const fmtDue = (t) => {
    if (!t || !t.due_date) return '';
    const d = new Date(t.due_date);
    return isNaN(d.getTime()) ? '' : 'Due ' + d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Rank (prototype V3PriorityStack): sent-back (blocking) → in-progress → ready
  // (due-soonest). Waiting-on-others tasks are excluded — they're not actionable
  // by the user right now.
  self.ranked = computed(() => {
    const list = (getTasks() || []).map((t) => ({ t, status: statusOf(t) }));
    const returned = list.filter((x) => x.status === 'returned');
    const inProgress = list.filter((x) => x.status === 'in-progress');
    const ready = list.filter((x) => x.status === 'ready').sort((a, b) => (dueSoon(b.t) ? 1 : 0) - (dueSoon(a.t) ? 1 : 0));
    return [...returned, ...inProgress, ...ready];
  });
  self.total = computed(() => self.ranked.value.length);
  self.hasItems = computed(() => self.total.value > 0);
  // Prototype is a fixed top-3 stack — never an expandable list.
  self.hasMore = computed(() => false);

  const toCard = (x, i) => {
    const t = x.t;
    const status = x.status;
    const meta = taskMeta(t.task_type || t.doc_type);
    const sentBack = status === 'returned';
    const resuming = status === 'in-progress';
    const tone = sentBack ? 'revise' : resuming ? 'go' : 'start';
    const pill = SPILL[status] || SPILL.ready;
    return {
      task: t,
      id: t.document_task_id ?? t.doc_id ?? i,
      reference: t.reference,
      system_number: t.system_number,
      title: t.doc_title || t.doc_name || t.reference || '',
      type: t.task_type || t.doc_type || '',
      meta: [t.level, fmtDue(t)].filter(Boolean).join(' · '),
      status,
      tone,
      icon: sentBack ? 'assignment_return' : resuming ? 'play_circle' : 'arrow_forward',
      cta: sentBack ? 'Revise' : (resuming ? 'Continue ' : 'Start ') + meta.verb,
      lead: i === 0,
      // lifecycle stepper
      stepIdx: LIFECYCLE_IDX[status] != null ? LIFECYCLE_IDX[status] : 1,
      stepTone: tone,
      stepCount: STEP_COUNT,
      // solid status pill
      pillLabel: pill.label,
      pillTone: pill.tone,
      pillIcon: pill.icon,
      // sent-back feedback (inline expandable) or the resume line. The reviewer's
      // note rides on the task's create-activity comment (`comment`). Overflow
      // ("does it exceed the 2-line clamp?") is DOM-measured by the widget,
      // exactly like the prototype — no length heuristic here.
      feedback: sentBack ? t.comment || t.feedback || t.task_feedback || '' : '',
      feedbackBy: t.feedback_by || t.feedbackBy || 'Reviewer',
      resumeLine: resuming ? 'Saved — pick up where you left off.' : '',
    };
  };

  self.cards = computed(() => self.ranked.value.map(toCard));
  self.shown = computed(() => self.cards.value.slice(0, limit()));

  return self;
}
